import type { PartnersLevel } from '~~/types/partnersLevels/partnersLevels';

export type BasketPartnersPoints = {
  points: number;
  // set when the order takes the customer to a higher level
  reachedLevelNumber: number | null;
  // set when the customer stays on their level and a higher one exists
  nextLevel: { levelNumber: number; missingPoints: number } | null;
};

type BasketPartnersPointsInput = {
  basketTotal: number;
  ratio: number;
  // null when the customer's points are unknown
  currentPoints: number | null;
  levels: readonly PartnersLevel[];
};

const getLevelNumberAt = (levels: readonly PartnersLevel[], points: number): number | null =>
  levels.filter(({ minPoints }) => minPoints <= points).at(-1)?.levelNumber ?? null;

/**
 * Estimates the points an order adds the way the Customer Data Platform credits them: the gross amount
 * divided by the shop's ratio, rounded down.
 */
export const getBasketPartnersPoints = (
  { basketTotal, ratio, currentPoints, levels }: BasketPartnersPointsInput,
): BasketPartnersPoints => {

  const points = Math.floor(basketTotal / ratio);

  if (currentPoints === null) {

    return { points, reachedLevelNumber: null, nextLevel: null };

  }

  const pointsAfterOrder = currentPoints + points;
  const levelAfterOrder = getLevelNumberAt(levels, pointsAfterOrder);

  if (levelAfterOrder !== getLevelNumberAt(levels, currentPoints)) {

    return { points, reachedLevelNumber: levelAfterOrder, nextLevel: null };

  }

  const nextLevel = levels.find(({ minPoints }) => minPoints > pointsAfterOrder);

  return {
    points,
    reachedLevelNumber: null,
    nextLevel: nextLevel
      ? { levelNumber: nextLevel.levelNumber, missingPoints: nextLevel.minPoints - pointsAfterOrder }
      : null,
  };

};
